// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track how many calculators have been used this session
let calcUsedCount = 0;
export const incrementCalcUsed = () => { calcUsedCount++; };

const NewsletterPostCalcBanner = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) return; // Logged in users don't need this
    const dismissed = sessionStorage.getItem('newsletter_dismissed');
    if (dismissed) return;

    // Check every 2 seconds if threshold reached
    const interval = setInterval(() => {
      const count = parseInt(sessionStorage.getItem('sap_calc_count') || '0');
      if (count >= 3) {
        setVisible(true);
        clearInterval(interval);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [user]);

  const dismiss = () => {
    sessionStorage.setItem('newsletter_dismissed', 'true');
    setVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'post_calculator_banner' }),
      });
      setSubmitted(true);
      if (window.gtag) window.gtag('event', 'newsletter_signup', { source: 'post_calculator_banner' });
      setTimeout(() => {
        sessionStorage.setItem('newsletter_dismissed', 'true');
        setVisible(false);
      }, 3000);
    } catch {
      dismiss();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-[9990]"
          style={{
            background: '#252523',
            borderTop: '1px solid rgba(201,168,76,0.2)',
          }}
          data-testid="newsletter-post-calc-banner"
        >
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-4 px-6 py-4">
            {submitted ? (
              <div className="flex items-center gap-2 text-[#3D7A52] w-full justify-center">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Perfetto! Ti terremo aggiornato.</span>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#888780] shrink-0">
                  <span className="text-[#E8E8E4] font-medium">Stai usando i calcolatori?</span> Ricevi tips finanziari settimanali.
                </p>
                <form onSubmit={handleSubmit} className="flex flex-1 gap-2 min-w-0">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="La tua email"
                    required
                    className="flex-1 min-w-0 px-4 py-2 rounded-xl text-sm text-[#E8E8E4] placeholder-[#888780]"
                    style={{ background: '#1C1C1A', border: '1px solid rgba(255,255,255,0.08)' }}
                    data-testid="newsletter-banner-email"
                  />
                  <button type="submit" className="sapiens-btn-primary px-4 py-2 text-sm whitespace-nowrap" data-testid="newsletter-banner-submit">
                    Iscriviti
                  </button>
                </form>
              </>
            )}
            <button onClick={dismiss} className="text-[#888780] hover:text-[#E8E8E4] shrink-0" data-testid="newsletter-banner-dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewsletterPostCalcBanner;
